
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  languages,
  DEFAULT_LANGUAGE,
  getLanguageFromPath,
  getLocalizedPath,
  type LanguageCode,
} from './languages';

import { translations } from './translations';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export function LanguageProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

    const pathLanguage = getLanguageFromPath(
      window.location.pathname
    );

    // A language in the URL takes priority.
    if (pathLanguage !== DEFAULT_LANGUAGE) {
      return pathLanguage;
    }

    // Restore manually selected language.
    const savedLanguage = localStorage.getItem('pipe-language');

    if (
      savedLanguage &&
      languages.some((item) => item.code === savedLanguage)
    ) {
      return savedLanguage as LanguageCode;
    }

    // English for all first-time visitors.
    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    const selectedLanguage = languages.find(
      (item) => item.code === language
    );

    document.documentElement.lang = language;
    document.documentElement.dir = selectedLanguage?.dir ?? 'ltr';

    localStorage.setItem('pipe-language', language);
  }, [language]);

  useEffect(() => {
    const handlePopState = () => {
      setLanguageState(
        getLanguageFromPath(window.location.pathname)
      );
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const setLanguage = (nextLanguage: LanguageCode) => {
    const nextPath = getLocalizedPath(
      window.location.pathname,
      nextLanguage
    );

    window.history.pushState({}, '', nextPath);
    setLanguageState(nextLanguage);
  };

  const t = (key: string): string => {
    return (
      translations[language]?.[key] ??
      translations.en[key] ??
      key
    );
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      'useLanguage must be used inside LanguageProvider'
    );
  }

  return context;
}
